// MUST stay first: installs a `console.warn` filter for known
// deprecated-API noise from third-party libraries. ES imports are
// hoisted, so any code emitted at import-time by later `import`
// statements only sees the filter if this side-effect import runs
// first. See `silenceWarnings.ts` for the filtered prefixes.
import './silenceWarnings';
// Boot-time theme bootstrap. The shared `preferencesStore` reads the
// persisted user choice from localStorage (key
// `com.ping.web.preferences`) and applies the matching theme to
// <html> — both the legacy `data-theme="dark"` attribute (drives the
// CSS-var palette in design-system/tokens.css) AND the literal `dark`
// class (drives NativeWind's `dark:` utilities + the css-interop
// runtime). Importing the module for its side effect runs the
// store's constructor which calls `applyTheme` once before React
// renders, so the first paint is already in the right palette.
// The Settings modal v2 host (apps/web/src/shell/hosts/
// SettingsModalHost.tsx) calls `preferencesStore.setTheme(...)` when
// the user changes their preference; that handler re-runs
// `applyTheme` and persists, so a reload restores the choice.
import './core/preferences/PreferencesStore';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { StyleSheet } from 'react-native';

import { pingToastConfig,PingToastProvider } from '@ping/ui';

import { registerAppServiceWorker } from './core/push/registerAppServiceWorker';
import { captureInviteFromLocation } from './features/chat/inviteLink';
import { installOutboxDrainer } from './features/chat/sendOutbox';
import { showPostSignOutCleaningIfMarked } from './session/cleaningOverlay';
import { recentReloadReasons } from './session/reloadGuard';
import App from './App';

import '@fontsource/lato/400.css';
import '@fontsource/lato/700.css';
import './design-system/tokens.css';
import './design-system/tailwind.css';
// Runtime accent recolor — must load AFTER tailwind.css so it can re-point
// the brand utility classes at `--ping-accent`. Inert until PreferencesStore
// sets `html[data-ping-accent]` for a non-default accent.
import './design-system/accent-override.css';
// "Chat background" appearance setting — paints the chat thread backdrop
// under `html[data-chat-bg]`. Inert until PreferencesStore sets it.
import './design-system/chat-background.css';

// NativeWind v4's runtime (react-native-css-interop) defaults to
// "media"-query dark mode, which conflicts with our manual
// `data-theme="dark"` strategy on <html>. Flipping the flag to "class"
// at boot makes the CSS interop watch for the dark-mode selector
// configured in tailwind.config.cjs (`darkMode: ['selector',
// '[data-theme="dark"]']`) instead of `prefers-color-scheme`. Without
// this, css-interop throws "Cannot manually set color scheme, as dark
// mode is type 'media'" the first time a dark-mode class is observed.
// `setFlag` is added to the StyleSheet object at module-load time by
// nativewind's import-source swap, so it's safe to call here.
(
  StyleSheet as unknown as {
    setFlag?: (key: string, value: string) => void;
  }
).setFlag?.('darkMode', 'class');

// Surface any app-initiated reloads from THIS tab's recent history up-front, so
// a reload loop's cause is visible on every load (even after a console clear).
// An EMPTY list while the page is visibly reloading points at an EXTERNAL
// reloader (e.g. Vite dev re-optimizing deps), not our recovery paths.
{
  const reasons = recentReloadReasons();
  if (reasons.length > 0) {
     
    console.warn('[ping] recent app reloads this tab:', reasons);
  }
}

// Capture an invite deep link (`/u/<id>` or `/g/<approver>/<groupId>`) BEFORE
// React renders — the router replaceState's unknown paths to `/chat` in a mount
// effect, which on a cold link-open wipes the path long before the auth-gated
// ChatRouteHost can read it. Stashing here preserves it across bootstrap + auth.
captureInviteFromLocation();

// Continuity for an explicit sign-out: `forceCleanReload` hard-navigates with a
// one-shot `?_signedout=` marker. If we just landed from one, keep the
// "Cleaning up…" overlay up briefly over the freshly-booting sign-in page (then
// strip the marker + fade) so the sign-out reads as one continuous action
// rather than an abrupt jump. Runs before React mounts so there's no flash.
showPostSignOutCleaningIfMarked();

// Wire the offline send OUTBOX drainer once for the app's lifetime. It replays
// any message queued while offline as soon as the session is authenticated +
// the socket reconnects (and on `online`). No-op until then, so calling it here
// at boot is safe regardless of auth state.
installOutboxDrainer();

// Register the offline app-shell service worker (production only). Lets the app
// open with no network after the first visit; the message data is already
// served offline from IndexedDB.
registerAppServiceWorker();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <App />
    {/* Mount the toast provider as a sibling of <App /> so it
        survives across every App-internal route / phase transition.
        `react-native-toast-message` is a singleton — multiple
        renderers would steal each other's `Toast.show()` calls.
        Custom minimal, theme-aware renderers via `pingToastConfig`. */}
    <PingToastProvider config={pingToastConfig} />
  </React.StrictMode>,
);
